import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Card,
  Row,
  Col,
  Button,
  Progress,
  Typography,
  Statistic,
  Tag,
  Table,
  Space,
  Alert,
  Spin,
  message,
  Modal,
  Input
} from 'antd';
import {
  ShoppingCartOutlined,
  CheckCircleOutlined,
  GiftOutlined,
  HistoryOutlined,
  ShopOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  SendOutlined
} from '@ant-design/icons';
import partnerService from '../../services/partnerService';
import BackButton from '../../components/BackButton';
import './ItemPackagesPage.scss';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const ItemPackagesPage = observer(() => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [itemsInfo, setItemsInfo] = useState(null);
  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [requesting, setRequesting] = useState(false);
  const [requestModalVisible, setRequestModalVisible] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [requestNotes, setRequestNotes] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);

    const [infoResult, packagesResult, historyResult] = await Promise.all([
      partnerService.fetchItemsInfo(),
      partnerService.fetchAvailablePackages(),
      partnerService.fetchPackageHistory()
    ]);

    if (infoResult.success) {
      setItemsInfo(infoResult.data);
    }
    if (packagesResult.success) {
      setPackages(packagesResult.data);
    }
    if (historyResult.success) {
      setHistory(historyResult.data);
    }

    setLoading(false);
  };

  const handleRequestBundle = (pkg) => {
    setSelectedPackage(pkg);
    setRequestNotes('');
    setRequestModalVisible(true);
  };

  const submitBundleRequest = async () => {
    if (!selectedPackage) return;

    setRequesting(true);
    const result = await partnerService.requestBundle(selectedPackage.package_type, requestNotes);

    if (result.success) {
      message.success(t('partner.packages.messages.requestSubmitted'));
      setRequestModalVisible(false);
      loadData();
    } else {
      message.error(result.error || t('partner.packages.messages.requestFailed'));
    }
    setRequesting(false);
  };

  const historyColumns = [
    {
      title: t('partner.packages.tableHeaders.package'),
      dataIndex: 'package_type_display',
      key: 'package_type_display',
    },
    {
      title: t('partner.packages.tableHeaders.extraItems'),
      dataIndex: 'extra_items',
      key: 'extra_items',
      render: (items) => <Tag color="blue">+{items}</Tag>
    },
    {
      title: t('common.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors = {
          APPROVED: 'green',
          PENDING_APPROVAL: 'orange',
          REJECTED: 'red',
          EXPIRED: 'default'
        };
        const icons = {
          APPROVED: <CheckCircleOutlined />,
          PENDING_APPROVAL: <ClockCircleOutlined />,
          REJECTED: <CloseCircleOutlined />,
          EXPIRED: null
        };
        const statusMap = {
          APPROVED: t('partner.packages.status.approved'),
          PENDING_APPROVAL: t('partner.packages.status.pending'),
          REJECTED: t('partner.packages.status.rejected'),
          EXPIRED: t('partner.packages.status.expired')
        };
        return (
          <Tag color={colors[status] || 'default'} icon={icons[status]}>
            {statusMap[status] || status}
          </Tag>
        );
      }
    },
    {
      title: t('partner.packages.tableHeaders.requestedAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => date ? new Date(date).toLocaleDateString() : '-'
    },
    {
      title: t('partner.packages.tableHeaders.approvedAt'),
      dataIndex: 'approved_at',
      key: 'approved_at',
      render: (date) => date ? new Date(date).toLocaleDateString() : '-'
    }
  ];

  // Bundle pricing data matching the image provided
  const bundlePricing = [
    {
      key: 'basic',
      name: t('partner.packages.bundles.basic'),
      totalListings: 10,
      extraItems: 0,
      monthlyPrice: 0,
      yearlyPrice: 0,
      isIncluded: true,
      package_type: null
    },
    {
      key: 'BUNDLE_5',
      name: 'Bundle 5',
      totalListings: 15,
      extraItems: 5,
      monthlyPrice: 10,
      yearlyPrice: 100,
      package_type: 'BUNDLE_5'
    },
    {
      key: 'BUNDLE_10',
      name: 'Bundle 10',
      totalListings: 20,
      extraItems: 10,
      monthlyPrice: 15,
      yearlyPrice: 150,
      package_type: 'BUNDLE_10'
    },
    {
      key: 'BUNDLE_15',
      name: 'Bundle 15',
      totalListings: 25,
      extraItems: 15,
      monthlyPrice: 20,
      yearlyPrice: 200,
      package_type: 'BUNDLE_15'
    },
    {
      key: 'BUNDLE_40',
      name: 'Bundle 40',
      totalListings: 50,
      extraItems: 40,
      monthlyPrice: 50,
      yearlyPrice: 500,
      package_type: 'BUNDLE_40'
    }
  ];

  const bundleColumns = [
    {
      title: t('partner.packages.bundles.bundle'),
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <span style={{ fontWeight: 600 }}>
          {name}
          {record.isIncluded && (
            <Tag color="green" style={{ marginLeft: 8 }}>
              {t('partner.packages.bundles.included')}
            </Tag>
          )}
        </span>
      )
    },
    {
      title: t('partner.packages.bundles.totalListings'),
      dataIndex: 'totalListings',
      key: 'totalListings',
      align: 'center',
      render: (total, record) => (
        <span>
          {record.key === 'BUNDLE_40' ? t('partner.packages.bundles.upTo') + ' ' : ''}{total}
          {record.extraItems > 0 && (
            <Text type="secondary" style={{ marginLeft: 4 }}>
              (+{record.extraItems})
            </Text>
          )}
        </span>
      )
    },
    {
      title: t('partner.packages.bundles.monthlyPrice'),
      dataIndex: 'monthlyPrice',
      key: 'monthlyPrice',
      align: 'center',
      render: (price, record) => (
        <span style={{ fontWeight: 500 }}>
          {record.isIncluded ? t('partner.packages.bundles.included') : `${price}€/${t('partner.packages.bundles.month')}`}
        </span>
      )
    },
    {
      title: t('partner.packages.bundles.yearlyPrice'),
      dataIndex: 'yearlyPrice',
      key: 'yearlyPrice',
      align: 'center',
      render: (price, record) => (
        <span style={{ fontWeight: 500 }}>
          {record.isIncluded ? '0€' : `${price}€/${t('partner.packages.bundles.year')}`}
        </span>
      )
    },
    {
      title: '',
      key: 'action',
      align: 'center',
      render: (_, record) => {
        if (record.isIncluded) {
          return <Tag color="success">{t('partner.packages.bundles.active')}</Tag>;
        }

        // Check if there's a pending request for this bundle type
        const hasPendingRequest = itemsInfo?.pending_requests?.some(
          req => req.package_type === record.package_type
        );

        if (hasPendingRequest) {
          return (
            <Tag color="orange" icon={<ClockCircleOutlined />}>
              {t('partner.packages.status.pending')}
            </Tag>
          );
        }

        return (
          <Button
            type="primary"
            size="small"
            icon={<SendOutlined />}
            onClick={() => handleRequestBundle(record)}
          >
            {t('partner.packages.bundles.request')}
          </Button>
        );
      }
    }
  ];

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const usagePercent = itemsInfo ? (itemsInfo.current_items / itemsInfo.max_items) * 100 : 0;
  const isNearLimit = usagePercent >= 80;

  return (
    <div className="item-packages-page" style={{ padding: 24 }}>
      <BackButton to="/partner/dashboard" label={t('partner.packages.backToDashboard')} />

      <Title level={2}>
        <ShopOutlined /> {t('partner.packages.title')}
      </Title>
      <Paragraph type="secondary">
        {t('partner.packages.description')}
      </Paragraph>

      {/* Usage Overview */}
      <Card title={t('partner.packages.currentUsage')} style={{ marginBottom: 24 }}>
        <Row gutter={24}>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 16 }}>
              <Text strong>{t('partner.products.itemUsage')}</Text>
              <Progress
                percent={Math.round(usagePercent)}
                status={isNearLimit ? 'exception' : 'active'}
                strokeColor={isNearLimit ? '#ff4d4f' : '#1890ff'}
              />
              <Text type="secondary">
                {itemsInfo?.current_items || 0} / {itemsInfo?.max_items || 10} {t('partner.packages.itemsUsed')}
              </Text>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <Row gutter={16}>
              <Col span={8}>
                <Statistic
                  title={t('partner.packages.currentItems')}
                  value={itemsInfo?.current_items || 0}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title={t('partner.packages.maxItems')}
                  value={itemsInfo?.max_items || 10}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title={t('partner.packages.remaining')}
                  value={itemsInfo?.items_remaining || 0}
                  valueStyle={{ color: itemsInfo?.items_remaining <= 2 ? '#ff4d4f' : '#1890ff' }}
                />
              </Col>
            </Row>
          </Col>
        </Row>

        {itemsInfo?.purchased_extra_items > 0 && (
          <Alert
            message={t('partner.packages.purchasedExtraSlots', { count: itemsInfo.purchased_extra_items })}
            type="success"
            showIcon
            icon={<GiftOutlined />}
            style={{ marginTop: 16 }}
          />
        )}

        {isNearLimit && (
          <Alert
            message={t('partner.packages.lowOnSlots')}
            description={t('partner.packages.lowOnSlotsDesc')}
            type="warning"
            showIcon
            style={{ marginTop: 16 }}
          />
        )}

        {itemsInfo?.pending_requests?.length > 0 && (
          <Alert
            message={t('partner.packages.pendingRequestAlert')}
            description={t('partner.packages.pendingRequestDesc')}
            type="info"
            showIcon
            icon={<ClockCircleOutlined />}
            style={{ marginTop: 16 }}
          />
        )}
      </Card>

      {/* Bundle Pricing Table */}
      <Card
        title={
          <Space>
            <ShoppingCartOutlined />
            <span>{t('partner.packages.bundlePricing')}</span>
          </Space>
        }
        style={{ marginBottom: 24 }}
      >
        <Table
          dataSource={bundlePricing}
          columns={bundleColumns}
          rowKey="key"
          pagination={false}
          className="bundle-pricing-table"
        />
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">
            {t('partner.packages.bundleNote')}
          </Text>
        </div>
      </Card>

      {/* Request History */}
      <Card
        title={
          <Space>
            <HistoryOutlined />
            <span>{t('partner.packages.requestHistory')}</span>
          </Space>
        }
      >
        {history.length > 0 ? (
          <Table
            dataSource={history}
            columns={historyColumns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Text type="secondary">{t('partner.packages.noPackages')}</Text>
          </div>
        )}
      </Card>

      {/* Request Bundle Modal */}
      <Modal
        title={t('partner.packages.requestModal.title')}
        open={requestModalVisible}
        onCancel={() => setRequestModalVisible(false)}
        onOk={submitBundleRequest}
        confirmLoading={requesting}
        okText={t('partner.packages.requestModal.submit')}
        cancelText={t('common.cancel')}
      >
        {selectedPackage && (
          <>
            <Paragraph>
              {t('partner.packages.requestModal.description', {
                bundle: selectedPackage.name,
                items: selectedPackage.extraItems
              })}
            </Paragraph>

            <div style={{
              background: '#f5f5f5',
              padding: 16,
              borderRadius: 8,
              marginBottom: 16
            }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title={t('partner.packages.bundles.monthlyPrice')}
                    value={selectedPackage.monthlyPrice}
                    suffix={`€/${t('partner.packages.bundles.month')}`}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title={t('partner.packages.bundles.yearlyPrice')}
                    value={selectedPackage.yearlyPrice}
                    suffix={`€/${t('partner.packages.bundles.year')}`}
                  />
                </Col>
              </Row>
            </div>

            <div style={{ marginBottom: 8 }}>
              <Text strong>{t('partner.packages.requestModal.notesLabel')}</Text>
              <Text type="secondary" style={{ marginLeft: 4 }}>
                ({t('common.optional')})
              </Text>
            </div>
            <TextArea
              rows={3}
              value={requestNotes}
              onChange={(e) => setRequestNotes(e.target.value)}
              placeholder={t('partner.packages.requestModal.notesPlaceholder')}
            />

            <Alert
              message={t('partner.packages.requestModal.approvalNote')}
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          </>
        )}
      </Modal>
    </div>
  );
});

export default ItemPackagesPage;
